import React from 'react'

import { Typography } from '@fe-toolkit/web-components/ssr'
import { Box, type TMarginProps, type TSxProp } from '@fe-toolkit/web-components'
import {
  DetailsListItem,
  DetailsListItemProps,
  DetailsListSeparator,
  DetailsListSeparatorProps,
} from '@giffgaff/broadband-common'

import LeftAlignedAlert from '@/components/molecules/LeftAlignedAlert'

export type DetailsListItems = Array<TypedDetailsListItemProps>

export type DetailsListProps = TMarginProps & {
  items: DetailsListItems
  separatorColour?: DetailsListSeparatorProps['colour']
  sx?: TSxProp
}

export type TypedDetailsListItemProps = {
  type?: 'listItem' | 'alert'
  alertVariant?: 'info' | 'warning' | 'error'
} & DetailsListItemProps

type DetailsListViewModel = {
  key: string
  separatorColour?: DetailsListSeparatorProps['colour']
  item: TypedDetailsListItemProps
  displayValue: DetailsListItemProps['value']
  type: 'listItem' | 'alert'
}

const getDisplayValue = (value: DetailsListItemProps['value']) => (Array.isArray(value) ? value.join(' ') : value)

const getItemType = (item: TypedDetailsListItemProps) => item.type ?? 'listItem'

const buildViewModel = (
  items: DetailsListItems,
  separatorColour?: DetailsListSeparatorProps['colour'],
): DetailsListViewModel[] =>
  items.map((item, index) => ({
    key: item.label,
    separatorColour: index < items.length - 1 ? separatorColour : undefined,
    item,
    displayValue: getDisplayValue(item.value),
    type: getItemType(item),
  }))

const renderListItem = (item: TypedDetailsListItemProps, displayValue: DetailsListItemProps['value']) => {
  return (
    <DetailsListItem
      description={item.description}
      label={item.label}
      maskSensitiveDescription={item.maskSensitiveDescription}
      maskSensitiveValue={item.maskSensitiveValue}
      value={displayValue}
    />
  )
}

const renderAlert = (item: TypedDetailsListItemProps, displayValue: DetailsListItemProps['value']) => {
  return (
    <LeftAlignedAlert isMinor borderRadius="small" variant={item.alertVariant}>
      <Typography as="p" variant="speakUp">
        {item.label}
      </Typography>
      <Typography as="p" variant="speak">
        {displayValue}
      </Typography>
    </LeftAlignedAlert>
  )
}

const renderItemContent = (
  item: TypedDetailsListItemProps,
  displayValue: DetailsListItemProps['value'],
  itemType: string,
) => {
  switch (itemType) {
    case 'alert':
      return renderAlert(item, displayValue)
    case 'listItem':
    default:
      return renderListItem(item, displayValue)
  }
}

const DetailsList = ({ items, separatorColour, ...props }: DetailsListProps) => (
  <Box {...props}>
    {buildViewModel(items, separatorColour).map(viewModel => (
      <React.Fragment key={viewModel.key}>
        {renderItemContent(viewModel.item, viewModel.displayValue, viewModel.type)}
        {viewModel.separatorColour && <DetailsListSeparator colour={viewModel.separatorColour} />}
      </React.Fragment>
    ))}
  </Box>
)

export default DetailsList
